import * as THREE from 'three';
import CustomShaderMaterial from 'three-custom-shader-material/vanilla';
import grassShader from './shaders/grass.js';
import { createHorizonHazeMaterial } from './materials/horizonHazeMaterial.js';
import { getHeight } from './terrainConfig.js';

export class TerrainManager {

    // Constants
    #chunkSize = 400;
    #viewRadius = 5;
    #unloadRadius = 6;
    #chunkVertexCount = 11;
    #instanceCount = 7000;
    #GRASS_MESH_ENVMAP_INTENSITY = 0.35;
    // Limits how many terrain chunks can be generated per animation frame.
    // Without this cap, the first frame would synchronously generate all ~120 missing
    // chunks at once (geometry creation + simplex noise per vertex), causing a hard freeze.
    // At 4 per frame the world fills in smoothly over ~30 frames instead.
    #maxChunksPerFrame = 4;

    #specialChunks = {
        //"0,3": { type: "ground", materialColor: 0x000000, vertexCount: 5, grassBladeCount: 1000, texture: null }
    };

    #GRASS_LOD_LEVELS = [
        { distanceSq: 100 * 100, multiplier: 1.0 },
        { distanceSq: 150 * 150, multiplier: 1.0 },
        { distanceSq: 200 * 200, multiplier: 0.95 },
        { distanceSq: 250 * 250, multiplier: 0.92 },
        { distanceSq: 300 * 300, multiplier: 0.80 },
        { distanceSq: 350 * 350, multiplier: 0.70 },
        { distanceSq: 400 * 400, multiplier: 0.60 },
        { distanceSq: 450 * 450, multiplier: 0.50 },
        { distanceSq: 500 * 500, multiplier: 0.40 },
        { distanceSq: 600 * 600, multiplier: 0.30 },
        { distanceSq: 700 * 700, multiplier: 0.2 },
        { distanceSq: 800 * 800, multiplier: 0.1 },
        { distanceSq: 900 * 900, multiplier: 0.1 },
        { distanceSq: 1000 * 1000, multiplier: 0.01 },
        { distanceSq: 1200 * 1200, multiplier: 0.01 },
        { distanceSq: Infinity, multiplier: 0.001 }
    ];

    // State
    #loadedChunks = new Map();
    #grassWorkerPool = [];
    #grassWorkerQueue = [];
    #allGrassComputed = false;
    // Tracks how many grass worker tasks are currently in-flight (queued or being processed).
    // Used to set allGrassComputed only when every task has truly finished, not just when
    // the queue happens to be empty (which can happen while workers are still running).
    #pendingWorkerCount = 0;

    // References
    #worldScene;
    #camera;
    #bladeGeometry;
    #grassMaterial;
    #groundMaterial;
    #halfChunkSize;

    constructor({ worldScene, camera }) {
        this.#worldScene = worldScene;
        this.#camera = camera;
        this.#halfChunkSize = this.#chunkSize * 0.5;

        // Create grass blade geometry
        this.#bladeGeometry = new THREE.ShapeGeometry(this.#createGrassBladeShape());

        // Create grass material
        this.#grassMaterial = new CustomShaderMaterial({
            baseMaterial: THREE.MeshStandardMaterial,
            uniforms: {
                time: { value: 0.0 },
                cameraPos: { value: camera.position }
            },
            vertexShader: grassShader.vert,
            fragmentShader: grassShader.frag,
            vertexColors: false,
            side: THREE.DoubleSide,
            fog: true
        });
        // envMapIntensity is set once on the shared material here rather than inside the worker
        // callback. Setting it there (once per chunk response) would trigger material.needsUpdate
        // on the same shared material object every time a worker finishes, causing repeated GPU
        // shader recompiles during initial load.
        this.#grassMaterial.envMapIntensity = this.#GRASS_MESH_ENVMAP_INTENSITY;

        // Create ground material
        this.#groundMaterial = createHorizonHazeMaterial({
            baseColor: new THREE.Color(0x000000),
            envMap: worldScene.envMap,
            hazeStart: 1300,
            hazeEnd: 1700,
            horizonHeight: 0.5,
            hazeIntensity: 0.65
        });

        // Both material env maps are updated in a single skyboxPromise callback so each material
        // recompiles its shader exactly once. Previously the ground material was updated here and
        // the grass material was updated inside the worker reply handler — that meant one recompile
        // per loaded chunk (up to 120+) instead of one recompile total.
        worldScene.skyboxPromise.then((texture) => {
            if (this.#grassMaterial) {
                this.#grassMaterial.envMap = worldScene.envMap;
                this.#grassMaterial.needsUpdate = true;
            }
            if (this.#groundMaterial && this.#groundMaterial.uniforms) {
                this.#groundMaterial.uniforms.uEnvMap.value = texture;
                this.#groundMaterial.needsUpdate = true;
            }
        });

        // Initialize worker pool
        this.#initWorkerPool();
    }

    // Whether all queued grass worker tasks have finished
    get allGrassComputed() {
        return this.#allGrassComputed;
    }

    // Generate the starting grid of terrain chunks (sizeInChunks x sizeInChunks)
    loadInitialTerrain(sizeInChunks) {
        for (let z = 0; z < sizeInChunks; z++) {
            for (let x = 0; x < sizeInChunks; x++) {
                const chunkKey = `${x},${z}`;
                if (!this.#loadedChunks.has(chunkKey)) {
                    const chunkMesh = this.#generateChunk(x, z);
                    this.#worldScene.scene.add(chunkMesh);
                    // chunkX and chunkZ are stored in the chunk data so that the per-frame
                    // unload and LOD methods can read them directly rather than splitting and
                    // parsing the string key on every chunk every frame.
                    this.#loadedChunks.set(chunkKey, { terrainMesh: chunkMesh, chunkX: x, chunkZ: z });
                }
            }
        }
    }

    // Per-frame update: haze camera uniform, chunk streaming, grass LOD and wind
    update(playerPosition, deltaTime) {
        // Update ground material camera position for horizon haze effect
        this.#groundMaterial.uniforms.uCameraPosition.value.copy(playerPosition);

        // Dynamically update chunks
        this.#loadChunksAroundPlayer(playerPosition);
        this.#adjustGrassInstanceCount(playerPosition);
        this.#unloadFarChunks(playerPosition);

        // Update time for wind animation
        this.#grassMaterial.uniforms.time.value += deltaTime * 1.0;
    }

    // Build the tapered triangle Shape used for each grass blade
    #createGrassBladeShape() {
        const shape = new THREE.Shape();
        shape.moveTo(-0.074, 0);
        shape.lineTo(0.074, 0);
        shape.lineTo(0, 2.2);
        shape.lineTo(-0.074, 0);
        return shape;
    }

    // Spawn web workers (one per CPU core) for off-thread grass placement
    #initWorkerPool() {
        const workerPoolSize = navigator.hardwareConcurrency || 4;

        for (let i = 0; i < workerPoolSize; i++) {
            const worker = new Worker(new URL('grassWorker.js', import.meta.url), { type: 'module' });
            worker.onmessage = (event) => this.#handleWorkerMessage(event, worker);
            this.#grassWorkerPool.push(worker);
        }
    }

    // Receive computed grass data from a worker and create the InstancedMesh
    #handleWorkerMessage(event, worker) {
        const { chunkKey, grassGeometryData } = event.data;

        const grassGeometry = new THREE.InstancedBufferGeometry();
        grassGeometry.copy(this.#bladeGeometry);

        grassGeometry.setAttribute('instanceRotationMatrix', new THREE.InstancedBufferAttribute(grassGeometryData.rotationMatrices, 9));
        grassGeometry.setAttribute('scale', new THREE.InstancedBufferAttribute(grassGeometryData.scales, 1));
        grassGeometry.setAttribute('offset', new THREE.InstancedBufferAttribute(grassGeometryData.offsets, 3));

        const grassMesh = new THREE.InstancedMesh(grassGeometry, this.#grassMaterial, grassGeometryData.instanceCount);

        grassMesh.geometry.boundingSphere = grassGeometryData.boundingSphere;
        // envMap and envMapIntensity are intentionally NOT set here. All grass meshes share
        // the same #grassMaterial instance, so setting envMap here would call needsUpdate on
        // the shared material once per worker reply, causing a shader recompile per chunk.
        // Instead, both are set once in the constructor after the skybox loads.

        this.#worldScene.scene.add(grassMesh);

        if (this.#loadedChunks.has(chunkKey)) {
            this.#loadedChunks.get(chunkKey).grassMesh = grassMesh;
        }

        // Decrement to reflect this task completing. If more tasks are queued, hand the next
        // one directly to this worker so it stays busy without returning to the pool first.
        // The queued task's count was already incremented when it was enqueued in
        // #postToGrassWorker, so we must NOT increment again here — only decrement for the
        // task that just finished.
        this.#pendingWorkerCount--;

        if (this.#grassWorkerQueue.length > 0) {
            const nextTask = this.#grassWorkerQueue.shift();
            worker.postMessage(nextTask);
        } else {
            this.#grassWorkerPool.push(worker);
        }

        // Only mark completion when every in-flight task has finished. Previously the flag
        // was set as soon as the queue drained, which could happen while several workers were
        // still computing their current task — causing allGrassComputed to fire too early.
        if (this.#pendingWorkerCount === 0) {
            this.#allGrassComputed = true;
        }
    }

    // Send a task to an idle worker, or queue it if all workers are busy
    #postToGrassWorker(task) {
        // Increment before dispatching so the counter is always >= the number of active tasks.
        // allGrassComputed is reset to false because new work is now in flight — the flag will
        // be set back to true once this (and any other pending) tasks complete.
        this.#pendingWorkerCount++;
        this.#allGrassComputed = false;

        if (this.#grassWorkerPool.length > 0) {
            const worker = this.#grassWorkerPool.pop();
            worker.postMessage(task);
        } else {
            this.#grassWorkerQueue.push(task);
        }
    }

    // Create a terrain mesh and dispatch grass generation for one chunk
    #generateChunk(x, z) {
        const offsetX = x * this.#chunkSize;
        const offsetZ = z * this.#chunkSize;

        const chunkKey = `${x},${z}`;
        const isSpecialChunk = this.#specialChunks.hasOwnProperty(chunkKey);

        const chunkProps = isSpecialChunk
            ? this.#specialChunks[chunkKey]
            : { type: "default", materialColor: 0x000000, grassBladeCount: this.#instanceCount };

        // A deterministic seed derived from chunk coordinates. XOR-combining two large primes
        // gives a good spread with no collisions for reasonable chunk ranges. The >>> 0 coerces
        // the result to an unsigned 32-bit integer as expected by mulberry32.
        // This ensures the worker produces identical grass positions every time this chunk loads,
        // preventing visible popping when a chunk is unloaded and re-entered.
        const seed = ((x * 73856093) ^ (z * 19349663)) >>> 0;
        this.#postToGrassWorker({ chunkKey, offsetX, offsetZ, chunkSize: this.#chunkSize, instanceCount: chunkProps.grassBladeCount, seed });

        const baseGeometry = new THREE.PlaneGeometry(this.#chunkSize, this.#chunkSize, this.#chunkVertexCount, this.#chunkVertexCount);
        baseGeometry.rotateX(-Math.PI / 2);
        baseGeometry.translate(this.#chunkSize / 2, 0, this.#chunkSize / 2);

        // The bounding sphere center must be the midpoint of the geometry in local space.
        // After translate(chunkSize/2, 0, chunkSize/2), vertex X and Z both span [0, chunkSize],
        // so the center is (chunkSize/2, 0, chunkSize/2). The previous value was (chunkSize, 0,
        // chunkSize) — the far corner — which placed the sphere off-center and broke Three.js
        // frustum culling, causing all terrain chunks to be submitted to the GPU every frame
        // even when they were clearly outside the camera's view.
        const chunkBoundingSphere = new THREE.Sphere(
            new THREE.Vector3(this.#chunkSize / 2, 0.0, this.#chunkSize / 2),
            this.#chunkSize * 1.0
        );
        baseGeometry.boundingSphere = chunkBoundingSphere;

        const vertices = baseGeometry.attributes.position.array;
        for (let i = 0; i < vertices.length; i += 3) {
            const vertexX = vertices[i] + offsetX;
            const vertexZ = vertices[i + 2] + offsetZ;
            vertices[i + 1] = getHeight(vertexX, vertexZ);
        }

        const chunkMesh = new THREE.Mesh(baseGeometry, this.#groundMaterial);
        chunkMesh.position.set(offsetX, 0, offsetZ);

        return chunkMesh;
    }

    // Generate missing chunks within viewRadius of the player, at most #maxChunksPerFrame per call.
    // This is called every frame, so without the cap it would try to generate all ~120 missing
    // chunks synchronously on the first frame — each requiring geometry allocation and simplex
    // noise evaluation per vertex — causing a hard freeze before the first render.
    #loadChunksAroundPlayer(playerPosition) {
        const playerChunkX = Math.floor(playerPosition.x / this.#chunkSize);
        const playerChunkZ = Math.floor(playerPosition.z / this.#chunkSize);

        let generated = 0;
        for (let dz = -this.#viewRadius; dz <= this.#viewRadius; dz++) {
            for (let dx = -this.#viewRadius; dx <= this.#viewRadius; dx++) {
                if (generated >= this.#maxChunksPerFrame) return;

                const chunkX = playerChunkX + dx;
                const chunkZ = playerChunkZ + dz;
                const chunkKey = `${chunkX},${chunkZ}`;

                if (!this.#loadedChunks.has(chunkKey)) {
                    const chunkMesh = this.#generateChunk(chunkX, chunkZ);
                    this.#worldScene.scene.add(chunkMesh);
                    this.#loadedChunks.set(chunkKey, { terrainMesh: chunkMesh, chunkX, chunkZ });
                    generated++;
                }
            }
        }
    }

    // Remove and dispose chunks beyond unloadRadius from the player
    #unloadFarChunks(playerPosition) {
        const playerChunkX = Math.floor(playerPosition.x / this.#chunkSize);
        const playerChunkZ = Math.floor(playerPosition.z / this.#chunkSize);

        this.#loadedChunks.forEach((chunk, key) => {
            // chunkX/chunkZ were stored when the chunk was inserted, avoiding key.split(",").map(Number)
            // on every chunk every frame (which created allocations and GC pressure at 100+ chunks).
            const { chunkX, chunkZ } = chunk;
            const distance = Math.max(Math.abs(chunkX - playerChunkX), Math.abs(chunkZ - playerChunkZ));

            if (distance > this.#unloadRadius) {
                this.#worldScene.scene.remove(chunk.terrainMesh);
                chunk.terrainMesh.geometry.dispose();
                if (chunk.grassMesh) {
                    this.#worldScene.scene.remove(chunk.grassMesh);
                    chunk.grassMesh.geometry.dispose();
                }
                this.#loadedChunks.delete(key);
            }
        });
    }

    // Reduce visible grass instances on distant chunks for performance
    #adjustGrassInstanceCount(playerPosition) {
        const playerX = playerPosition.x;
        const playerZ = playerPosition.z;

        this.#loadedChunks.forEach((chunk, key) => {
            if (this.#specialChunks.hasOwnProperty(key) || !chunk.grassMesh) {
                return;
            }

            // Same as above — use stored coords rather than splitting the key string.
            const { chunkX, chunkZ } = chunk;

            const centerX = chunkX * this.#chunkSize + this.#halfChunkSize;
            const centerZ = chunkZ * this.#chunkSize + this.#halfChunkSize;

            const deltaX = playerX - centerX;
            const deltaZ = playerZ - centerZ;
            const distanceSq = deltaX * deltaX + deltaZ * deltaZ;

            for (let i = 0; i < this.#GRASS_LOD_LEVELS.length; i++) {
                if (distanceSq <= this.#GRASS_LOD_LEVELS[i].distanceSq) {
                    const newCount = Math.floor(this.#instanceCount * this.#GRASS_LOD_LEVELS[i].multiplier);

                    if (chunk.grassMesh.count !== newCount) {
                        chunk.grassMesh.count = newCount;
                    }
                    break;
                }
            }
        });
    }
}
